import { cx, sortCx } from '../../../utils/cx';

export const styles = sortCx({
    common: {
        menuItem: cx(
            'relative w-full outline-hidden hit-slop data-[click-only-trigger]:[&_[data-icon="trailing"]]:hidden',
            // cursor and interaction states
            'cursor-pointer data-[disabled]:cursor-default',
        ),
        shell: cx(
            'flex min-w-0 items-center rounded-sm outline-hidden select-none',
            'data-[open]:bg-active',
            // Highlight keyboard focus like hover, but not pointer-modality focus: submenu open/close
            // parks real focus on items, so styling plain [data-focused] leaves a row painted after
            // the cursor leaves the menu (WEB-16864).
            'data-[hovered]:bg-primary_hover',
            'data-[focus-visible]:bg-primary_hover',
            'data-[focus-visible]:ring-2 data-[focus-visible]:ring-focus-ring data-[focus-visible]:ring-inset',
            // icon styles
            '[&_[data-icon="leading"]_svg]:size-2xl [&_[data-icon="trailing"]_svg]:size-xl,',
        ),
        row: 'flex min-w-0 flex-1 items-center gap-2',
        content: 'flex min-w-0 flex-1 flex-col justify-center',
        label: 'truncate font-medium whitespace-nowrap',
        description: 'truncate whitespace-nowrap',
        leading: 'inline-flex shrink-0',
        trailing: 'ml-auto inline-flex shrink-0 items-center',
        leadingIcon: 'size-2xl',
        trailingIcon: 'size-xl',
    },
    sizes: {
        xs: {
            shell: 'px-2.5 py-1 text-md gap-md',
        },
        sm: {
            shell: 'px-2.5 py-1.5 text-md gap-md',
        },
        md: {
            shell: 'px-2.5 py-2 text-md gap-md',
        },
        lg: {
            shell: 'px-2.5 py-2 text-lg gap-md',
        },
    },
    variants: {
        default: {
            root: 'text-secondary data-[hovered]:text-secondary_hover data-[focus-visible]:text-secondary_hover data-[selected]:text-primary',
            description: 'text-inherit',
            icon: 'text-fg-quaternary',
            trailing: 'text-secondary',
        },
        destructive: {
            root: 'text-error-primary',
            description: 'text-inherit',
            icon: 'text-error-secondary',
            trailing: 'text-error-secondary',
        },
        disabled: {
            root: 'text-disabled',
            description: 'text-inherit',
            icon: 'text-inherit',
            trailing: 'text-inherit',
        },
    },
});

type ResolveMenuItemVariantStylesProps = {
    destructive?: boolean;
    isDisabled?: boolean;
};

/**
 * Resolves the visual variant for a menu item from its semantic state.
 */
export const resolveMenuItemVariantStyles = ({ destructive, isDisabled }: ResolveMenuItemVariantStylesProps) => {
    if (isDisabled) return styles.variants.disabled;
    if (destructive) return styles.variants.destructive;
    return styles.variants.default;
};
